import type { TFunction } from 'i18next';
import DeleteIcon from '../../assets/icons/delete.svg?react';
import EditIcon from '../../assets/icons/edit.svg?react';
import type { PatientRoomPropertyResponse, PatientRoomResponse } from '../../api/generated';
import { EditableCell, EditingActions, PropertiesCell, tertiaryIconButtonStyle } from '../UI';
import type { TableColumn } from '../UI';
import { Button } from '../UI/base/Button';
import columnStyles from './roomColumns.module.css';

interface RoomColumnCallbacks {
  editingRoomId: string | null;
  editingName: string;
  openPickerRoomId: string | null;
  allRoomProperties: PatientRoomPropertyResponse[];
  onStartEditName: (roomId: string) => void;
  onNameChange: (value: string) => void;
  onSaveName: () => void;
  onCancelEdit: () => void;
  onOpenPicker: (roomId: string) => void;
  onClosePicker: () => void;
  onAddProperty: (roomId: string, propertyId: string) => void;
  onRemoveProperty: (roomId: string, propertyId: string) => void;
  onDelete: (room: PatientRoomResponse) => void;
}

export const getRoomColumns = (
  t: TFunction,
  cb: RoomColumnCallbacks
): TableColumn<PatientRoomResponse>[] => [
  {
    key: 'name',
    header: t('settings-col-room-name'),
    width: 237,
    cellStyle: { whiteSpace: 'nowrap' },
    render: (room) => (
      <EditableCell
        value={room.description}
        isEditing={cb.editingRoomId === room.id}
        name={cb.editingName}
        onNameChange={cb.onNameChange}
        onSave={cb.onSaveName}
        onCancel={cb.onCancelEdit}
      />
    )
  },
  {
    key: 'needs',
    header: t('settings-col-room-needs'),
    cellStyle: { flex: '1 1 0', flexBasis: 0, minWidth: 200 },
    render: (room) => (
      <PropertiesCell
        assignedProperties={room.patientRoomProperties}
        allProperties={cb.allRoomProperties}
        emptyLabel={t('room-need-no-results')}
        isPickerOpen={cb.openPickerRoomId === room.id}
        isEditing={cb.editingRoomId === room.id}
        onOpenPicker={() => cb.onOpenPicker(room.id)}
        onClosePicker={cb.onClosePicker}
        onAdd={(propertyId) => cb.onAddProperty(room.id, propertyId)}
        onRemove={(propertyId) => cb.onRemoveProperty(room.id, propertyId)}
      />
    )
  },
  {
    key: 'edit',
    header: t('settings-col-edit'),
    width: 100,
    headerAlign: 'center',
    cellStyle: { alignItems: 'center' },
    render: (room) => {
      const isEditingThisRow = cb.editingRoomId === room.id;

      if (isEditingThisRow) {
        const canSave = cb.editingName.trim().length > 0;
        return (
          <EditingActions canSave={canSave} onSave={cb.onSaveName} onCancel={cb.onCancelEdit} />
        );
      }

      return (
        <Button
          type="tertiary"
          onClick={() => cb.onStartEditName(room.id)}
          styles={{ root: tertiaryIconButtonStyle }}
        >
          <EditIcon className={columnStyles.actionIcon} />
        </Button>
      );
    }
  },
  {
    key: 'delete',
    header: t('settings-col-delete'),
    width: 100,
    headerAlign: 'center',
    cellStyle: { alignItems: 'center' },
    render: (room) => (
      <Button
        type="tertiary"
        onClick={() => cb.onDelete(room)}
        styles={{ root: tertiaryIconButtonStyle }}
      >
        <DeleteIcon className={columnStyles.actionIcon} />
      </Button>
    )
  }
];
